"use client";

import { cn } from "@gigpro/ui/tailwind/cn";
import { Dialog as DialogPrimitive } from "radix-ui";
import type * as React from "react";
import { Close } from "../../Icons";
import { usePortalScope } from "../PortalScope";

const DialogRoot = DialogPrimitive.Root;

const DialogOverlay = ({ ref, className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 grid place-items-center bg-black/60 data-[state=closed]:animate-out data-[state=closed]:delay-100 data-[state=closed]:duration-200 data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:duration-300 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export interface DialogProps extends DialogContentProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  /**
   * Mobile only
   * Renders the dialog as a bottom sheet
   */
  bottomSheet?: boolean;
  className?: string;
  contentClassName?: string;
}

const Dialog = ({ ref, open, onOpenChange, onClose, ...props }: DialogProps) => (
  <DialogPrimitive.Root
    open={open}
    onOpenChange={(open) => {
      if (onOpenChange) onOpenChange(open);
      else if (!open && onClose) onClose();
    }}
  >
    <DialogContent ref={ref} {...props} />
  </DialogPrimitive.Root>
);
Dialog.displayName = DialogPrimitive.Root.displayName;
interface DialogContentProps
  extends React.ComponentProps<typeof DialogPrimitive.Content>, Pick<DialogProps, "contentClassName" | "bottomSheet"> {
  blockInteraction?: boolean;
  closeOnBackdropClick?: boolean;
}
const DialogContent = ({
  ref,
  className,
  contentClassName,
  children,
  bottomSheet,
  blockInteraction,
  closeOnBackdropClick,
  ...props
}: DialogContentProps) => {
  const scope = usePortalScope();
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay onClick={blockInteraction ? (e) => e.stopPropagation() : undefined}>
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "relative z-50 flex max-h-full min-h-0 w-full max-w-lg min-w-0 rounded-sm bg-white shadow-up outline-none data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-300",
            bottomSheet
              ? "min-h-0 max-w-none self-end rounded-b-none align-bottom data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom sm:bottom-auto sm:max-w-lg sm:self-center sm:rounded-b sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=closed]:fade-out-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=open]:fade-in-0 sm:data-[state=open]:zoom-in-95"
              : "data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            className,
          )}
          onInteractOutside={closeOnBackdropClick ? undefined : (e) => e.preventDefault()}
          {...scope}
          {...props}
        >
          <div
            className={cn(
              "flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto p-4",
              contentClassName,
            )}
          >
            {children}
          </div>
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPrimitive.Portal>
  );
};
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "sticky -bottom-4 z-10 -mx-4 mt-auto -mb-4 flex justify-end gap-1 rounded-sm bg-white px-3.5 py-4",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTrigger = DialogPrimitive.Trigger;

const DialogClose = ({ ref, className, ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) => (
  <DialogPrimitive.Close
    ref={ref}
    className={cn("size-4 text-brand focus:outline-none disabled:pointer-events-none", className)}
    data-testid="btn_close_dialog"
    {...props}
  >
    <Close className="size-full" />
    <span className="sr-only">Close</span>
  </DialogPrimitive.Close>
);
DialogClose.displayName = "DialogClose";

interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  hideCloseButton?: boolean;
}
const DialogHeader = ({ className, children, hideCloseButton, ...props }: DialogHeaderProps) => (
  <div
    className={cn(
      "sticky -top-4 z-10 -mx-4 -mt-4 flex flex-col gap-1.5 rounded-sm bg-white p-4 pr-10 text-left",
      className,
    )}
    {...props}
  >
    {children}
    {!hideCloseButton && <DialogClose className="absolute top-4 right-4" />}
  </div>
);
DialogHeader.displayName = "DialogHeader";

const DialogTitle = ({
  ref,
  className,
  description,
  hideCloseButton,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title> & {
  description?: React.ReactNode;
  hideCloseButton?: boolean;
}) => (
  <DialogHeader className={className} hideCloseButton={hideCloseButton}>
    <DialogPrimitive.Title ref={ref} className="text-large leading-none font-semibold tracking-tight" {...props} />
    <DialogPrimitive.Description
      className={cn("mt-0.5 text-normal tracking-tight text-gray", !description && "hidden")}
    >
      {description}
    </DialogPrimitive.Description>
  </DialogHeader>
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export { Dialog as DialogPrimitive } from "radix-ui";

export { DialogRoot, Dialog, DialogFooter, DialogTitle, DialogHeader, DialogClose, DialogContent, DialogTrigger };
